import PropTypes from 'prop-types'

import { plural } from '@aytm/app/stats/lib/helpers'
import style from '@aytm/app/stats/page/data-quality-report/styles/sections/HumanReview.module.scss'

// Copy kept as JS strings (not inline JSX text) so apostrophes need no escaping.
const REJECT_COPY =
  "We take your feedback seriously and limit the ability of the rejected responses' authors to participate in future surveys."
const KUDOS_COPY =
  'Thank you! By giving Kudos to the most thoughtful responses, you help motivate respondents to continue leaving meaningful answers on future surveys. We pay a surprise extra bonus to the most useful respondents as a gesture of appreciation.'
// Neither figure is ever stated as a bare zero: a report full of zeroes reads as unfinished, and the
// two counts are unrelated, so each heading rewords on its own while the other keeps its figure.
const NO_REJECTIONS_HEADING = "You haven't rejected any responses yet"
const NO_KUDOS_HEADING = "You haven't given Kudos to any panelists yet"
// The expert block reports our own review rather than the author's actions, so its zero states an
// outcome instead of an opening: "yet" would read as more removals still to come on a report the
// author opens for reassurance. The reason the other form carries moves to the copy below.
const NO_EXPERT_REMOVALS_HEADING = "We haven't removed any responses from your survey"
const EXPERT_COPY =
  'As a part of our quality control process, your survey was selected for an additional review by our team. An expert-driven manual data quality check ensures survey responses are accurate, consistent, and meaningful. Trained researchers review open-ended answers to detect inattentive or fraudulent patterns. This human-in-the-loop approach ensures that final datasets meet the highest standards of integrity.'

const Block = ({ testid, glyph, iconClass, heading, copy }) => (
  <div className={style.block} data-testid={testid}>
    <div className={style.heading}>
      <span className={[style.icon, iconClass].filter(Boolean).join(' ')}>
        <i className={`icon-aytmfd-${glyph}`} />
      </span>
      <span className={style.headingText}>{heading}</span>
    </div>
    <p className={style.copy}>{copy}</p>
  </div>
)

Block.propTypes = {
  testid: PropTypes.string,
  glyph: PropTypes.string.isRequired,
  iconClass: PropTypes.string,
  heading: PropTypes.string.isRequired,
  copy: PropTypes.string.isRequired
}

const HumanReview = ({ data }) => {
  // The hide rule lives in the payload mapper (PRD Part E) so it has a single source of truth.
  const { clientRejectedCount = 0, kudosCount = 0, expertRemovedCount = 0, showExpertReview = true } = data

  return (
    <div data-testid="stat-dqr-human-review" className={style.review}>
      <Block
        testid="stat-dqr-human-review-rejected"
        glyph="close"
        iconClass={style.iconReject}
        heading={
          clientRejectedCount
            ? `You've rejected ${clientRejectedCount} ${plural(clientRejectedCount, 'response', 'responses')}`
            : NO_REJECTIONS_HEADING
        }
        copy={REJECT_COPY}
      />
      <Block
        testid="stat-dqr-human-review-kudos"
        glyph="heart"
        iconClass={style.iconKudos}
        heading={
          kudosCount
            ? `You've given Kudos to ${kudosCount} ${plural(kudosCount, 'panelist', 'panelists')}`
            : NO_KUDOS_HEADING
        }
        copy={KUDOS_COPY}
      />
      {showExpertReview ? (
        <Block
          testid="stat-dqr-human-review-expert"
          glyph="aytm-experts"
          iconClass={style.iconExpert}
          heading={
            expertRemovedCount
              ? `We have removed ${expertRemovedCount} ${plural(
                  expertRemovedCount,
                  'response as it was',
                  'responses as they were'
                )} not accurate`
              : NO_EXPERT_REMOVALS_HEADING
          }
          copy={EXPERT_COPY}
        />
      ) : null}
    </div>
  )
}

HumanReview.displayName = 'HumanReview'
HumanReview.propTypes = { state: PropTypes.string, tier: PropTypes.string, data: PropTypes.object }
HumanReview.defaultProps = { data: {} }
export default HumanReview
